import { useEffect, useState } from "react";
import { api } from "@/hub/lib/api";
import { Inbox, Send, MailOpen, Mail, Loader2, User } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const TEMPLATES = [
  { key: "confirmation", label: "Booking Confirmation" },
  { key: "reminder", label: "Payment Reminder" },
  { key: "followup", label: "Follow-up" },
];

export default function Mailbox() {
  const [messages, setMessages] = useState([]);
  const [sel, setSel] = useState(null);
  const [busy, setBusy] = useState("");

  const load = () => api.mailbox().then(setMessages);
  useEffect(() => { load(); }, []);

  const open = async (m) => {
    setSel(m);
    if (!m.read) {
      await api.mailboxRead(m.id);
      setMessages((l) => l.map((x) => (x.id === m.id ? { ...x, read: true } : x)));
    }
  };

  const send = async (template) => {
    if (!sel?.group_id) return;
    setBusy(template);
    try {
      await api.mailboxSend(sel.group_id, template);
      toast.success(`${TEMPLATES.find((t) => t.key === template)?.label} sent to client`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to send email");
    } finally {
      setBusy("");
    }
  };

  const unread = messages.filter((m) => !m.read).length;

  return (
    <div data-testid="mailbox-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-heading text-2xl font-semibold text-forest">Team Mailbox</h1>
          <p className="text-sm text-stone-500">Shared inbox for Admins and Relationship Managers — booking requests, website inquiries and sent emails.</p>
        </div>
        {unread > 0 && <span className="text-xs bg-terracotta text-white px-2.5 py-1 rounded-sm font-semibold" data-testid="mailbox-unread-badge">{unread} unread</span>}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        <div className="lg:col-span-5 bg-white border border-stone-200 rounded-sm divide-y divide-stone-100 max-h-[70vh] overflow-auto lush-scroll" data-testid="mailbox-list">
          {messages.length === 0 && (
            <div className="p-10 text-center text-sm text-stone-400">
              <Inbox className="h-8 w-8 mx-auto mb-3 text-stone-300" />
              No messages yet. Booking requests and website inquiries land here.
            </div>
          )}
          {messages.map((m) => (
            <button
              key={m.id}
              data-testid={`mailbox-item-${m.id}`}
              onClick={() => open(m)}
              className={`w-full text-left p-4 hover:bg-stone-50 transition-colors ${sel?.id === m.id ? "bg-forest/5 border-l-2 border-forest" : ""}`}
            >
              <div className="flex items-center gap-3">
                <span className={`shrink-0 h-8 w-8 rounded-sm flex items-center justify-center ${m.direction === "in" ? "bg-forest/10 text-forest" : "bg-terracotta/10 text-terracotta"}`}>
                  {m.direction === "in" ? (m.read ? <MailOpen className="h-4 w-4" /> : <Mail className="h-4 w-4" />) : <Send className="h-4 w-4" />}
                </span>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center justify-between gap-2">
                    <span className={`text-sm truncate ${m.read ? "text-stone-600" : "font-semibold text-forest"}`}>{m.subject}</span>
                    {!m.read && <span className="h-2 w-2 rounded-full bg-terracotta shrink-0" />}
                  </div>
                  <div className="text-[11px] text-stone-400 truncate">
                    {m.direction === "in" ? `From: ${m.from_name || m.from_email}` : `To: ${m.from_email}`} · {new Date(m.created_at).toLocaleString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}
                  </div>
                </div>
              </div>
            </button>
          ))}
        </div>

        <div className="lg:col-span-7 bg-white border border-stone-200 rounded-sm p-6 min-h-[300px]" data-testid="mailbox-detail">
          {!sel ? (
            <div className="h-full flex items-center justify-center text-sm text-stone-400">Select a message to read.</div>
          ) : (
            <div>
              <div className="flex items-start justify-between gap-4 border-b border-stone-100 pb-4 mb-4">
                <div>
                  <h2 className="font-heading text-lg font-semibold text-forest">{sel.subject}</h2>
                  <p className="text-xs text-stone-400 mt-1 flex items-center gap-1.5">
                    <User className="h-3 w-3" /> {sel.from_name || "—"} {sel.from_email && `<${sel.from_email}>`} · {new Date(sel.created_at).toLocaleString("en-GB")}
                  </p>
                </div>
                <span className={`text-[10px] uppercase tracking-wider px-2 py-1 rounded-sm ${sel.direction === "in" ? "bg-forest/10 text-forest" : "bg-terracotta/10 text-terracotta"}`}>
                  {sel.kind === "booking_request" ? "Booking Request" : sel.kind === "inquiry" ? "Inquiry" : "Sent"}
                </span>
              </div>
              <p className="text-sm text-stone-600 leading-relaxed whitespace-pre-wrap" data-testid="mailbox-body">{sel.body}</p>

              {sel.group_id && (
                <div className="mt-8 border-t border-stone-100 pt-5">
                  <p className="text-[10px] uppercase tracking-[0.15em] text-stone-400 mb-3">Email this client</p>
                  <div className="flex flex-wrap gap-2" data-testid="mailbox-send-actions">
                    {TEMPLATES.map((t) => (
                      <Button
                        key={t.key}
                        data-testid={`mailbox-send-${t.key}`}
                        onClick={() => send(t.key)}
                        disabled={!!busy}
                        className="rounded-sm bg-forest hover:bg-forest-hover text-xs"
                      >
                        {busy === t.key && <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" />}
                        {t.label}
                      </Button>
                    ))}
                  </div>
                  <p className="text-[11px] text-stone-400 mt-3">Templates use the client's details on file and go to their booking email.</p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
